import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { FormField, TextInput } from "../ui/FormField";
import { useLoyaltyConfig, useUpdateLoyaltyConfig } from "../../services/loyalty";

const schema = z.object({
  gbp_per_point: z
    .number({ error: "Please enter the GBP value per point" })
    .positive("Must be a positive number"),
  referrer_points: z
    .number({ error: "Please enter the referrer points" })
    .int("Must be a whole number")
    .min(0, "Must be 0 or more"),
  new_customer_reward_value: z
    .number({ error: "Please enter the new customer reward value" })
    .int("Must be a whole number")
    .min(0, "Must be 0 or more"),
});

type ConfigurePointsSchema = z.infer<typeof schema>;

interface ConfigurePointsFormProps {
  onSuccess?: () => void;
}

const ConfigurePointsForm = ({ onSuccess }: ConfigurePointsFormProps) => {
  const { data: config, isLoading } = useLoyaltyConfig();
  const { mutate: updateConfig, isPending } = useUpdateLoyaltyConfig();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ConfigurePointsSchema>({
    resolver: zodResolver(schema),
    defaultValues: { gbp_per_point: undefined },
  });

  useEffect(() => {
    if (config) {
      reset({
        gbp_per_point: config.point_value_in_pence,
        referrer_points: config.referrer_points,
        new_customer_reward_value: config.new_customer_reward_value,
      });
    }
  }, [config, reset]);

  const onSubmit = (data: ConfigurePointsSchema) => {
    updateConfig(
      {
        point_value_in_pence: data.gbp_per_point,
        referrer_points: data.referrer_points,
        new_customer_reward_type: config?.new_customer_reward_type ?? "points",
        new_customer_reward_value: data.new_customer_reward_value,
      },
      { onSuccess }
    );
  };

  const handleReset = () => {
    reset({
      gbp_per_point: config?.point_value_in_pence,
      referrer_points: config?.referrer_points,
      new_customer_reward_value: config?.new_customer_reward_value,
    });
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      className="bg-white rounded-[8px] px-[33px] pt-[33px] pb-[49px] flex flex-col gap-6"
      style={{
        border: "1px solid rgba(194,199,201,0.1)",
        boxShadow: "0px 20px 25px -5px rgba(0,0,0,0.1), 0px 8px 10px -6px rgba(0,0,0,0.1)",
      }}
    >
      <div className="flex flex-col gap-4">
        <h4 className="text-[13px] font-semibold uppercase tracking-[0.6px] text-brand-teal">
          Pounds to Points Conversion Rate
        </h4>
        <FormField label="GBP value of 1 point (£)" error={errors.gbp_per_point?.message}>
          <TextInput
            type="number"
            {...register("gbp_per_point", { valueAsNumber: true })}
            placeholder="e.g. 0.01"
            min={0}
            step="any"
            disabled={isLoading}
          />
        </FormField>
      </div>

      <hr className="border-border-subtle" />

      <div className="flex flex-col gap-4">
        <h4 className="text-[13px] font-semibold uppercase tracking-[0.6px] text-brand-teal">
          Referral Benefits
        </h4>
        <p className="text-[12px] text-content-label -mt-2">
          Benefits will be awarded only after the referee has completed the first purchase.
        </p>
        <FormField label="Points awarded to referrer" error={errors.referrer_points?.message}>
          <TextInput
            type="number"
            {...register("referrer_points", { valueAsNumber: true })}
            placeholder="e.g. 100"
            min={0}
            disabled={isLoading}
          />
        </FormField>
        <FormField label="Points awarded to referee" error={errors.new_customer_reward_value?.message}>
          <TextInput
            type="number"
            {...register("new_customer_reward_value", { valueAsNumber: true })}
            placeholder="e.g. 50"
            min={0}
            disabled={isLoading}
          />
        </FormField>
      </div>

      <div className="flex flex-col gap-3 pt-4">
        <button
          type="submit"
          disabled={isPending || isLoading}
          className="w-full h-[45px] rounded-[20px] text-white text-[16px] font-bold tracking-[0.64px] transition-opacity hover:opacity-90 disabled:opacity-60 bg-brand-accent"
        >
          {isPending ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
              Saving…
            </span>
          ) : "Save"}
        </button>
        <button
          type="button"
          onClick={handleReset}
          className="w-full py-3 rounded-[6px] text-[14px] font-semibold text-center transition-opacity hover:opacity-70 text-brand-teal"
        >
          Reset Changes
        </button>
      </div>
    </form>
  );
};

export default ConfigurePointsForm;
